/**
 * Activity Content Formatter
 * Transforms technical database content into user-friendly descriptions
 */

export interface Activity {
  type: 'note' | 'task' | 'call' | 'email' | 'meeting';
  content?: string;
  entity_type?: string;
  entity_id?: string;
  [key: string]: any;
}

/**
 * Removes technical jargon (UUIDs, record IDs, entity IDs) from activity content
 * and generates user-friendly descriptions
 */
export function formatActivityContent(activity: Activity): string {
  if (!activity) return '';
  
  let description = activity.content || '';
  
  // Remove all technical identifiers
  if (description) {
    // Remove UUID patterns (e.g., 3186198d-df4c-479e-9ba0-f26740b0f20d)
    description = description.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '');
    
    // Remove "record id" references
    description = description.replace(/record\s+id[:\s]*/gi, '');
    
    // Remove "entity_id" references
    description = description.replace(/entity[_\s]id[:\s]*/gi, '');
    
    // Remove "entity_type" references
    description = description.replace(/entity[_\s]type[:\s]*/gi, '');
    
    // Remove "organization_id" references
    description = description.replace(/organization[_\s]id[:\s]*/gi, '');
    
    // Clean up extra spaces and trim
    description = description.replace(/\s+/g, ' ').trim();
  }
  
  // If description is empty or too short after cleaning, generate a better one
  if (!description || description.length < 3) {
    switch (activity.type) {
      case 'note':
        return 'Added a note';
      case 'call':
        return 'Logged a phone call';
      case 'email':
        return 'Sent an email';
      case 'meeting':
        return 'Scheduled a meeting';
      case 'task':
        // Check if it's a follow-up task
        if (activity.content?.toLowerCase().includes('follow')) {
          return 'Created follow-up task';
        }
        return 'Created a task';
      default:
        return `Logged ${activity.type} activity`;
    }
  }
  
  return description;
}

/**
 * Gets a user-friendly label for the activity type
 */
export function getActivityTypeLabel(type: string): string {
  switch (type) {
    case 'note':
      return 'Note';
    case 'call':
      return 'Phone Call';
    case 'email':
      return 'Email';
    case 'meeting':
      return 'Meeting';
    case 'task':
      return 'Task';
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
}

/**
 * Gets an emoji icon for the activity type
 */
export function getActivityIcon(type: string): string {
  switch (type) {
    case 'note':
      return '📝';
    case 'call':
      return '📞';
    case 'email':
      return '✉️';
    case 'meeting':
      return '📅';
    case 'task':
      return '✅';
    default:
      return '📌';
  }
}

/**
 * Formats the entity reference in a user-friendly way
 */
export function formatEntityReference(entityType?: string, entityId?: string): string {
  if (!entityType) return '';
  
  const typeLabel = entityType.charAt(0).toUpperCase() + entityType.slice(1);
  
  // If we have an ID, we'd normally look it up, but for now just show the type
  return typeLabel;
}
